import { faFilePen, faPencil, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppBar,
    Box,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { useAuth } from '../../units/auth/AuthProvider';
import moment from 'moment';
import { Store } from '../../api/store';
import { api } from '../../api';

export const useEditClosedDialog = () => {
    const classes = useStyles();
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const [closedId, setClosedId] = useState('');
    const [inputMsg, setInputMsg] = useState<string>('');
    const [inputClosedFm, setInputClosedFm] = useState<Date>(new Date(moment().format('YYYY-MM-DD 09:30:00')));
    const [inputClosedTo, setInputClosedTo] = useState<Date>(new Date(moment().format('YYYY-MM-DD 20:30:00')));
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<string>('default');
    const [editMode, setEditMode] = useState<'create' | 'update'>('create');
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const [onCreateClosed, setOnCreateClosed] = useState<(closedFm: Date, closedTo: Date, msg: string, stroe: Store) => void>(
        () => () => {},
    );
    const [onUpdateClosed, setOnUpdateClosed] = useState<
        (closedId: string, closedFm: Date, closedTo: Date, msg: string, stroe: Store) => void
    >(() => () => {});
    const onReset = () => {
        setClosedId('');
        setInputMsg('');
        setInputClosedFm(new Date(moment().format('YYYY-MM-DD 09:30:00')));
        setInputClosedTo(new Date(moment().format('YYYY-MM-DD 20:30:00')));
        setStores([]);
        setSelectedStore('default');
    };
    const reloadStores = async () => {
        const newStores = await api.store.get({}).then((ret) => ret.data || []);
        setStores(newStores);
        if (newStores.length == 1) setSelectedStore(newStores[0].storeId!);
    };
    useEffect(() => {
        if (open) {
            reloadStores();
        } else {
            onReset();
        }
    }, [open]);
    useEffect(() => {
        closedId.length > 0 ? setEditMode('update') : setEditMode('create');
    }, [closedId]);
    return {
        classes,
        auth,
        alert,
        open,
        setOpen,
        closedId,
        setClosedId,
        inputMsg,
        setInputMsg,
        inputClosedFm,
        setInputClosedFm,
        inputClosedTo,
        setInputClosedTo,
        stores,
        setStores,
        selectedStore,
        setSelectedStore,
        editMode,
        setEditMode,
        onOpen,
        onClose,
        onCreateClosed,
        setOnCreateClosed,
        onUpdateClosed,
        setOnUpdateClosed,
        onReset,
    };
};

export interface EditClosedDialogProps extends ReturnType<typeof useEditClosedDialog> {}

export const EditClosedDialog = (props: EditClosedDialogProps): React.ReactElement => {
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={props.open} onClose={props.onClose}>
            <AppBar position='sticky' className={props.classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {props.closedId.length > 0 ? '更新店休時間' : '新增店休時間'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={props.onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Select
                                value={props.selectedStore}
                                onChange={(e) => props.setSelectedStore(e.target.value)}
                                fullWidth
                                style={{ margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            >
                                <MenuItem value={'default'} disabled>
                                    {'請選擇分店：'}
                                </MenuItem>
                                {props.stores.map((store) => (
                                    <MenuItem value={store.storeId}>{store.storeName}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                value={props.inputMsg}
                                onChange={(e) => props.setInputMsg(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'公告訊息'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='start'>
                                            <Button
                                                sx={{ minWidth: 36, maxWidth: 36 }}
                                                variant={'outlined'}
                                                onClick={() => {
                                                    const st = moment(props.inputClosedFm).format('YYYY-MM-DD A hh:mm');
                                                    const ed = moment(props.inputClosedTo).format('YYYY-MM-DD A hh:mm');
                                                    props.setInputMsg(`${st}～${ed} 暫停營業`);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPencil} fixedWidth />
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                type={'datetime-local'}
                                value={moment(props.inputClosedFm).format('YYYY-MM-DDTHH:mm')}
                                onChange={(e) => {
                                    if (e.target.value != '') {
                                        const newClosedFm = moment(e.target.value);
                                        const oldClosedTo = moment(props.inputClosedTo);
                                        props.setInputClosedFm(newClosedFm.toDate());
                                        props.setInputClosedTo(
                                            new Date(`${newClosedFm.format('YYYY-MM-DD')} ${oldClosedTo.format('HH:mm:ss')}`),
                                        );
                                    }
                                }}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'店休時間起'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                type={'datetime-local'}
                                value={moment(props.inputClosedTo).format('YYYY-MM-DDTHH:mm')}
                                onChange={(e) => {
                                    if (e.target.value != '') {
                                        const oldClosedFm = moment(props.inputClosedFm);
                                        const newClosedTo = moment(e.target.value);
                                        props.setInputClosedTo(
                                            new Date(`${oldClosedFm.format('YYYY-MM-DD')} ${newClosedTo.format('HH:mm:ss')}`),
                                        );
                                    }
                                }}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'店休時間迄'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            {props.editMode == 'create' ? (
                                <Button
                                    disabled={
                                        ![
                                            props.inputMsg.length > 0,
                                            props.inputClosedFm != undefined,
                                            props.inputClosedTo != undefined,
                                            props.selectedStore != 'default',
                                            moment(props.inputClosedFm).format('YYYY-MM-DD') ==
                                                moment(props.inputClosedTo).format('YYYY-MM-DD'),
                                            moment(props.inputClosedFm).unix() < moment(props.inputClosedTo).unix(),
                                        ].every((v) => v)
                                    }
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={() => {
                                        if (typeof props.onCreateClosed == 'function')
                                            props.onCreateClosed(props.inputClosedFm!, props.inputClosedTo!, props.inputMsg!, {
                                                storeId: props.selectedStore,
                                            });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} fixedWidth />
                                    {'確定新增'}
                                </Button>
                            ) : (
                                <Button
                                    disabled={
                                        ![
                                            props.inputMsg.length > 0,
                                            props.inputClosedFm != undefined,
                                            props.inputClosedTo != undefined,
                                            props.selectedStore != 'default',
                                            moment(props.inputClosedFm).format('YYYY-MM-DD') ==
                                                moment(props.inputClosedTo).format('YYYY-MM-DD'),
                                            moment(props.inputClosedFm).unix() < moment(props.inputClosedTo).unix(),
                                        ].every((v) => v)
                                    }
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={() => {
                                        if (typeof props.onUpdateClosed == 'function')
                                            props.onUpdateClosed(
                                                props.closedId,
                                                props.inputClosedFm!,
                                                props.inputClosedTo!,
                                                props.inputMsg!,
                                                {
                                                    storeId: props.selectedStore,
                                                },
                                            );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFilePen} fixedWidth />
                                    {'確定更新'}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};
